import {
  computed, defineAsyncComponent, inject, ref, type Component,
} from 'vue';
import AsteriskIcon from '@primeicons/vue/asterisk';
import BellIcon from '@primeicons/vue/bell';
import ClipboardIcon from '@primeicons/vue/clipboard';
import EnvelopeIcon from '@primeicons/vue/envelope';
import PhoneIcon from '@primeicons/vue/phone';
import TrashIcon from '@primeicons/vue/trash';
import UsersIcon from '@primeicons/vue/users';
import WrenchIcon from '@primeicons/vue/wrench';
import {
  addDays,
  addMinutes,
  compareDesc,
  endOfDay,
  formatDistanceToNowStrict,
  isAfter,
  isBefore,
  startOfDay,
  subMinutes,
} from 'date-fns';
import { useConfirm } from 'primevue/useconfirm';
import { useDialog } from 'primevue/usedialog';
import { useFetch } from './useFetch';
import { useEndpoint } from './useEndpoint';
import { useDateFns } from './useDateFns';
import { useUtilities } from './useUtilities';
import { useRoutingHelpers } from './useRoutingHelpers';
import { useToastNotifications } from './useToastNotifications';
import { useChangedFieldObserver } from './useChangedFieldObserver';
import { useSelectionsStore } from '@apparatix/stores/useSelectionsStore';
import { useStateSync } from './useStateSync';
import { useStringFunctions } from './useStringFunctions';
import { AsKeyed, AvailableViews } from '@apparatix/types';
import type {
  Activity,
  ActivityApiWrapper,
  ActivityFilter,
  ActivityFilterResponse,
  ActivityMethodName,
  ActivityModel,
  ActivityRelationship,
  ActivityReminder,
  ActivityTypeName,
  CreateOrUpdateActivityResponse,
  PendingRelationship,
  SavedActivityFilter,
  SavedFilterResponse,
} from '@apparatix/schemas/Activities';
import {
  activityFilter,
  activityFrontendSchema,
  editedActivityToRequestBody,
  hydrateActivityModel,
  modelToRequestBody,
  populateFeFieldsOnActivityResponse,
  ReminderTypes,
  savableActivityFilter,
} from '@apparatix/schemas/Activities';
import {
  ActivityAddedEvent,
  ActivityDeletedEvent,
  ActivityFields,
  ActivitySection,
  ActivityUpdatedEvent,
  ModalAction,
} from '@apparatix/leads/activities/commonProps';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';
import { StandardSelection } from '@apparatix/schemas/Selection';
import { DialogGroups, InjectEventNames } from '@apparatix/constants';
import type { ContactRelationship, Relationship, RelationshipType } from '@apparatix/schemas/Relationship';
import { CrmParentName, pluralToSingularTransformer, relationshipTypeToCrmDetail, relationshipTypeToCrmParent } from '@apparatix/schemas/Routing';
import { AppStore } from '@apparatix/stores/AppStore';
import { useRoute } from 'vue-router';
import { useCrmRelationshipsStore } from '@apparatix/stores/useCrmRelationshipsStore';

const DeferReminderComponent = defineAsyncComponent(() => import('@apparatix/leads/activities/DeferReminder.vue'));

// Fields that are available in the create/edit Activity (task) modal
const activityFields =
    ActivityFields.Title |
    ActivityFields.Method |
    ActivityFields.Purpose |
    ActivityFields.Date |
    ActivityFields.Reminder |
    ActivityFields.Note |
    ActivityFields.Relationships |
    ActivityFields.Files;

// Fields that are available in the create/edit Note modal
const noteFields = ActivityFields.Note | ActivityFields.Relationships;

// Fields that are available in the create/edit Reminder modal
const reminderFields =
    ActivityFields.Date |
    ActivityFields.Note |
    ActivityFields.Relationships |
    ActivityFields.Files |
    ActivityFields.ReminderType;

// Fields that are available in the edit AutoActivity modal (users cannot create this type themselves)
// const emailFields =
//   ActivityFields.Note |
//   ActivityFields.Relationships |
//   ActivityFields.Reminder;

export type GetActivityModelOptions = {
  type?: StandardSelection;
  accounts?: Relationship[];
  campaigns?: Relationship[];
  contacts?: Relationship[];
}

type ReminderTimeSelection = {
  label: string;
  value: number;
}

export type SupportedActivitiesView = Extract<AvailableViews, 'accounts' | 'contacts' | 'leads' | 'opportunities'>;

export type ActivityClickHandlerOptions = {
  type: ActivityTypeName;
  view: SupportedActivitiesView;
  parentObject?: ActivityRelationship;
  activity?: Activity;
}

type AddActivitySetupCallback = {
  (model: ActivityModel, changes?: Partial<ActivityModel>): void;
}

const Minutes = {
  count: (amount: number) => amount,
  fromHours: (hours: number) => 60 * hours,
  fromDays: (days: number) => 60 * 24 * days,
};

export const useActivities = () => {
  const getActivitiesEndpoint = useEndpoint<ActivityApiWrapper>({
    type: 'GET',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const activityFilterEndpoint = useEndpoint<ActivityApiWrapper>({
    type: 'POST',
    url: '/crm/activities/filter',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
  });

  const { getResults: deleteAllRemindersRequest } = useEndpoint<{ success?: string, error?: string }>({
    url: '/crm/activities/reminders/dismiss',
    type: 'DELETE',
    successField: 'success',
    toast: {
      showOnSuccess: false,
    },
  });

  const { getResults: viewAllRemindersRequest } = useEndpoint<{ success?: string, error?: string }>({
    url: '/crm/activities/reminders/viewed',
    type: 'PUT',
    successField: 'success',
    toast: {
      showOnSuccess: false,
    },
  });

  const getSavedFilterEndpoint = useEndpoint<SavedFilterResponse>({
    url: '/crm/activities/filter/saved',
    type: 'GET',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
  });

  const saveFilterEndpoint = useEndpoint<SavedFilterResponse>({
    url: '/crm/activities/filter/saved',
    type: 'POST',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
  });

  const resetFilterEndpoint = useEndpoint<{ success: boolean, error?: string }>({
    type: 'DELETE',
    successField: 'success',
    url: '/crm/activities/filter/reset',
    toast: {
      showOnSuccess: false,
    },
  });

  const { getResults: getEmailActivitySyncStatus } = useEndpoint<{ success: boolean, shouldSkipLegacyEmailActivity: boolean }>({
    type: 'GET',
    successField: 'success',
    url: '/crm/activities/email-sync-status',
    toast: {
      showOnSuccess: false,
      showOnError: false,
    },
  });

  const selections = useSelectionsStore();
  const relationshipStore = useCrmRelationshipsStore();

  const {
    adjustUtcToLocal,
    createArrayDateSorter,
    formatDate,
    isInCurrentYear,
    parse,
  } = useDateFns();

  const {
    arrayHasValues,
    asKeyedItems,
    combineArrays,
    deepClone,
    developerWarning,
    noop,
  } = useUtilities();

  const { observeChanges } = useChangedFieldObserver();
  const { showError, showSuccess } = useToastNotifications();
  const { openCrmDetailFlyout } = useRoutingHelpers();
  const { postFormData, destroy } = useFetch();
  const { subscribe } = useStateSync(AppStore.tenantId, AppStore.currentUser.id);
  const { isEmptyString, capitalize } = useStringFunctions();

  const confirm = useConfirm();
  const dialog = useDialog();
  const route = useRoute();

  const allActivities = ref<Activity[]>([]);
  const isLoading = ref(true);
  const currentAction = ref<ModalAction>();
  const modalTitle = ref('');

  subscribe<{ activity: Activity }>('ActivityUpdated', data => {
    const idx = allActivities.value?.findIndex(activity => activity.id === data.activity.id);
    if (idx !== -1) {
      allActivities.value[idx] = { ...allActivities.value[idx], ...data.activity };
    }
  });

  const onActivityCreated = inject<ActivityAddedEvent>(InjectEventNames.crm.activities.activityCreated, noop);
  const onActivityUpdated = inject<ActivityUpdatedEvent>(InjectEventNames.crm.activities.activityUpdated, noop);
  const onActivityDeleted = inject<ActivityDeletedEvent>(InjectEventNames.crm.activities.activityDeleted, noop);

  const reminderTimeSelections = ref<ReminderTimeSelection[]>([
    { label: 'At time of event', value: Minutes.count(0) },
    { label: '5 minutes before', value: Minutes.count(5) },
    { label: '15 minutes before', value: Minutes.count(15) },
    { label: '30 minutes before', value: Minutes.count(30) },
    { label: '1 hour before', value: Minutes.fromHours(1) },
    { label: '2 hours before', value: Minutes.fromHours(2) },
    { label: '12 hours before', value: Minutes.fromHours(12) },
    { label: '1 day before', value: Minutes.fromDays(1) },
    { label: '1 week before', value: Minutes.fromDays(7) },
  ]);

  const overduePredicate = (activity: Activity): boolean => {
    if (!activity?.startDateTime) {
      return false;
    }

    const activityStart = adjustUtcToLocal(parse(activity.startDateTime));

    const now = new Date();

    return isBefore(activityStart, now) && !activity.completedAt;
  };

  const upcomingPredicate = (activity: Activity): boolean => {
    if (!activity.startDateTime) {
      return false;
    }

    const activityStart = adjustUtcToLocal(parse(activity?.startDateTime));


    const now = new Date();

    return isAfter(activityStart, now) && !activity.completedAt;
  };

  const sortDatesDescending = (a: Activity, b: Activity) => {
    const aDate = parse(a.startDateTime);
    const bDate = parse(b.startDateTime);

    return compareDesc(aDate, bDate);
  };

  const getMostRecentOfCreatedOrUpdated = (activity: Activity) => {
    if (!activity?.createdAt && !activity?.updatedAt) {
      return undefined;
    }

    if (activity.createdAt === activity.updatedAt) {
      return parse(activity.createdAt);
    }

    const [newest] = [
      parse(activity.createdAt),
      parse(activity.updatedAt),
    ]
      .sort(compareDesc);

    return newest;
  };

  const sortHistory = (a: Activity, b: Activity) => {
    const aDate = a.startDateTime
      ? parse(a.startDateTime)
      : getMostRecentOfCreatedOrUpdated(a);

    const bDate = b.startDateTime
      ? parse(b.startDateTime)
      : getMostRecentOfCreatedOrUpdated(b);

    return compareDesc(aDate, bDate);
  };

  const overdue = computed(() => allActivities.value.filter(overduePredicate)?.toSorted(sortDatesDescending));

  const upcoming = computed(() => allActivities.value.filter(upcomingPredicate)?.toSorted(sortDatesDescending));

  const history = computed(() => {
    const overdueAndUpcoming = new Set([
      ...overdue.value.map(activity => activity.id),
      ...upcoming.value.map(activity => activity.id),
    ]);

    return allActivities.value.filter(activity => !overdueAndUpcoming.has(activity.id) && activity.completedAt)?.toSorted(sortHistory);
  });

  const activityTabSections = computed<Array<AsKeyed<ActivitySection>>>(() => asKeyedItems<ActivitySection>([
    {
      badgeSeverity: 'danger',
      items: asKeyedItems(overdue.value),
      label: 'Overdue',
    },
    {
      badgeSeverity: 'primary',
      items: asKeyedItems(upcoming.value),
      label: 'Upcoming',
    },
    {
      badgeSeverity: 'success',
      items: asKeyedItems(history.value),
      label: 'History',
    },
  ]));

  const modalFieldConfig = computed<ActivityFields>(() => {
    let fields: ActivityFields;

    switch (currentAction.value) {
      case 'create-activity':
        fields = activityFields;
        break;
      case 'update-activity':
        fields = activityFields | ActivityFields.Delete;
        break;
      case 'create-note':
        fields = noteFields;
        break;
      case 'update-note':
        fields = noteFields | ActivityFields.Delete;
        break;
      case 'create-reminder':
        fields = reminderFields;
        break;
      case 'update-reminder':
        fields = reminderFields | ActivityFields.Delete;
        break;
      default:
        fields = ActivityFields.None;
    }

    return fields;
  });

  const setCurrentAction = (type: ActivityTypeName, isEditing: boolean = false) => {
    switch (type) {
      case 'Autotask':
      case 'Task':
        currentAction.value = isEditing ? 'update-activity' : 'create-activity';
        modalTitle.value = isEditing ? 'Edit Activity' : 'Add Activity';
        break;
      case 'Reminder':
        currentAction.value = isEditing ? 'update-reminder' : 'create-reminder';
        modalTitle.value = isEditing ? 'Edit Reminder' : 'Add Reminder';
        break;
      case 'Note':
        currentAction.value = isEditing ? 'update-note' : 'create-note';
        modalTitle.value = isEditing ? 'Edit Note' : 'Add Note';
        break;
      default:
        currentAction.value = undefined;
        modalTitle.value = '';
    }
  };

  const loadActivities = async (parentObjectId: number, view: AvailableViews): Promise<void> => {
    switch (view) {
      case 'accounts':
        getActivitiesEndpoint.setEndpointUrl(`/crm/accounts/${parentObjectId}/activities`);
        break;
      case 'contacts':
        getActivitiesEndpoint.setEndpointUrl(`/crm/contacts/${parentObjectId}/activities`);
        break;
      case 'leads':
      case 'opportunities':
        getActivitiesEndpoint.setEndpointUrl(`/crm/opportunities/${parentObjectId}/activities`);
        break;
      default:
        throw new Error(`Activities are not supported in '${view}' context`);
    }

    const response = await getActivitiesEndpoint.getResults();

    if (arrayHasValues(response?.data?.activities)) {
      try {
        allActivities.value = response.data.activities.map(activity => populateFeFieldsOnActivityResponse.parse(activity));
      } catch (e) {
        developerWarning(true, e, 'throw');
      }
    }

    isLoading.value = false;
  };

  const getMostRecentlyCreatedRelationship = (relationships: Relationship[], ofType?: RelationshipType | RelationshipType[]) => {
    let filtered = relationships;

    if (ofType) {
      const types = Array.isArray(ofType) ? ofType : [ofType];
      filtered = filtered.filter(r => types.includes(r.type));
    }

    filtered = filtered.filter(r => !r.createdAt);
    if (filtered.length === 0) return null;
    const sorter = createArrayDateSorter<Relationship>('createdAt', 'ASC');

    return filtered.sort(sorter)[0];
  };

  const populatePendingRelationships = (available: Relationship[], model: ActivityModel) => {
    const existingAccounts = new Set(
      model.accounts?.map(account => account.id),
    );

    const existingCampaigns = new Set(
      model.campaigns?.map(camp => camp.id),
    );

    const existingContacts = new Set(
      model.contacts?.map(contact => contact.id),
    );

    const accountRelationships: PendingRelationship[] = available.filter(rel => rel.type === 'Account' && !existingAccounts.has(rel.id))
      .map(rel => ({
        key: crypto.randomUUID(),
        object: { ...rel },
        section: rel.type,
      }));

    const newestCampaign = getMostRecentlyCreatedRelationship(available.filter(rel => !existingCampaigns.has(rel.id)), ['Lead', 'Opportunity']);

    const campaignRelationships: PendingRelationship[] = newestCampaign
      ? [{ key: crypto.randomUUID(), object: { ...newestCampaign }, section: newestCampaign.type }]
      : [];

    const contacts = available.filter(rel => !existingContacts.has(rel.id) && rel.type === 'Contact') as ContactRelationship[];

    const primaryContact = contacts?.find(contact => contact.isPrimaryContact);

    const contactRelationships: PendingRelationship[] = primaryContact
      ? [{ key: crypto.randomUUID(), object: { ...primaryContact }, section: 'Contact' }]
      : contacts.map(rel => ({
        key: crypto.randomUUID(),
        object: { ...rel },
        section: rel.type,
      }));

    if (arrayHasValues(model.pendingRelationships)) {
      model.pendingRelationships.push(...accountRelationships);
      model.pendingRelationships.push(...campaignRelationships);
      model.pendingRelationships.push(...contactRelationships);
    } else {
      model.pendingRelationships = [
        ...accountRelationships,
        ...campaignRelationships,
        ...contactRelationships,
      ];
    }
  };

  const shouldShowSuccessToastOnSave = (activity: Activity) => {
    // Creating in global context where relationships aren't currently available
    if (!arrayHasValues(activity.accounts) && !arrayHasValues(activity.contacts) && !arrayHasValues(activity.campaigns)) {
      return true;
    }

    return false;
  };

  const getModel = (options: GetActivityModelOptions, editActivity?: Activity): [model: ActivityModel, changedFields: Partial<ActivityModel>] => {
    const generated = editActivity
      ? { ...createObjectFromSchema(activityFrontendSchema, options), ...deepClone(hydrateActivityModel.parse(editActivity)) }
      : createObjectFromSchema(activityFrontendSchema, options);

    if (!generated.method?.id) {
      generated.method = selections.activities.methods.find(method => method.name === 'Emails');
    }

    const includeInChanges: {[K in keyof ActivityModel]?: ActivityModel[K] } = {
      type: generated.type,
      method: generated.method,
    };

    Object.keys(generated).forEach(key => {
      if (arrayHasValues(generated[key])) {
        includeInChanges[key] = generated[key];
      }
    });

    if (generated.type.name === 'Reminder') {
      const toastType = selections.activities.reminderTypes.find(reminder => reminder.name === 'ApxToast');
      const dashboardType = selections.activities.reminderTypes.find(reminder => reminder.name === 'ApxDashboard');

      const toastReminder: ActivityReminder = {
        minutesBefore: 0,
        type: { ...toastType },
        checked: false,
      };

      const dashboardReminder: ActivityReminder = {
        minutesBefore: 0,
        type: { ...dashboardType },
        checked: false,
      };

      if (!arrayHasValues(generated.reminders)) {
        dashboardReminder.checked = true;
        generated.reminders = [{ ...toastReminder }, { ...dashboardReminder }];
      } else {
        let hasToastType = false;
        let hasDashboardType = false;

        generated.reminders.forEach(reminder => {
          if (reminder.type.id === toastType.id) {
            reminder.checked = true;
            hasToastType = true;
          }

          if (reminder.type.id === dashboardType.id) {
            reminder.checked = true;
            hasDashboardType = true;
          }
        });

        if (!hasToastType) {
          generated.reminders.push({ ...toastReminder });
        }

        if (!hasDashboardType) {
          generated.reminders.push({ ...dashboardReminder });
        }
      }
    }

    if (editActivity) {
      includeInChanges.id = editActivity.id;
      includeInChanges.accounts = editActivity.accounts;
      includeInChanges.campaigns = editActivity.campaigns;
      includeInChanges.contacts = editActivity.contacts;
    }

    const [changes, proxy] = observeChanges(generated, includeInChanges);

    return [proxy, changes];
  };

  const completeActivity = async (activity: Activity | number) => {
    const id = typeof activity === 'number' ? activity : activity.id;
    const payload = { id, completedAt: new Date().toISOString() };

    return createOrUpdateActivity(payload);
  };

  const getModelOptionsForEdit = (activity: Activity): GetActivityModelOptions => {
    const options: Partial<GetActivityModelOptions> = {};

    if (!arrayHasValues(activity.accounts)) {
      options.accounts = [];
    }

    if (!arrayHasValues(activity.campaigns)) {
      options.campaigns = [];
    }

    if (!arrayHasValues(activity.contacts)) {
      options.contacts = [];
    }

    return options;
  };

  const getModelOptions = (view: AvailableViews, parentObject: Relationship): GetActivityModelOptions => {
    if (!currentAction.value) {
      throw new Error('Call setCurrentAction() before building options');
    }

    const options: GetActivityModelOptions = {
      accounts: [],
      campaigns: [],
      contacts: [],
    };

    const association: Relationship[] = [{ ...parentObject }];

    switch (view) {
      case 'accounts':
        options.accounts = association;
        break;
      case 'contacts':
        options.contacts = association;
        break;
      case 'leads':
      case 'opportunities':
        options.campaigns = association;
        break;
      default:
        // Noop, adding Activities globally (like from speeddial) doesn't have a view
    }

    return options;
  };

  const getActivityType = (name: ActivityTypeName): StandardSelection =>
    selections.activities.types.find(selection => selection.name === name);

  const createOrUpdateActivity = async (modelValue: Partial<ActivityModel>, suppressToast?: boolean) => {
    if (!modelValue.id) {
      if (!modelValue.startDateTime) {
        modelValue.completedAt = new Date().toISOString();
      } else {
        const startDate = parse(modelValue.startDateTime);
        if (isBefore(startDate, new Date())) {
          modelValue.completedAt = new Date().toISOString();
        }
      }
    }

    try {
      const payload = modelValue.id
        ? editedActivityToRequestBody.parse(modelValue)
        : modelToRequestBody.parse(modelValue);

      const response = await postFormData<CreateOrUpdateActivityResponse>('/crm/activities', payload);

      if (response?.data?.activity) {
        const activity = populateFeFieldsOnActivityResponse.parse(response.data.activity);

        if (shouldShowSuccessToastOnSave(activity) && !suppressToast) {
          const displayType = !activity.type?.displayName || activity.type?.name === 'Task'
            ? 'Activity'
            : activity.type.displayName;

          const action = activity.updatedAt !== activity.createdAt ? 'updated' : 'created';

          showSuccess(`Successfully ${action} ${displayType}`, 'Success', 3000);
        }

        const shouldReload = arrayHasValues(modelValue.schedules);

        if (modelValue.id) {
          onActivityUpdated?.(activity, shouldReload);
        } else {
          onActivityCreated?.(activity, shouldReload);
        }

        return activity;
      }
    } catch (e) {
      showError('Unable to save Activity');
      developerWarning(true, e, 'throw');
    }
    return undefined;
  };

  const getIconForMethod = (methodName: ActivityMethodName): Component => {
    switch (methodName) {
      case 'Emails':
        return EnvelopeIcon;
      case 'Call':
        return PhoneIcon;
      case 'Meeting':
        return UsersIcon;
      case 'Other':
      default:
        return AsteriskIcon;
    }
  };

  const getIconForActivityType = (typeName: ActivityTypeName): Component => {
    const normalizedType = typeName?.toLowerCase();

    if (normalizedType === 'note') return ClipboardIcon;
    if (normalizedType === 'reminder') return BellIcon;
    if (normalizedType === 'system') return WrenchIcon;
    return AsteriskIcon;
  };

  const isPartOfSeries = (activity: Activity) => {
    if (!arrayHasValues(activity?.schedules)) return false;

    return activity.schedules.every(schedule => !isNaN(Number(schedule?.id)));
  };

  const isDeferrable = (activity: Activity) => {
    const reminderType = getActivityType('Reminder');

    if (activity?.id && reminderType && activity?.type?.id === reminderType.id) {
      return true;
    }

    if (!arrayHasValues(activity?.reminders)) {
      return false;
    }

    return activity.reminders.every(reminder => Boolean(reminder.id));
  };

  const validateOptions = (options: ActivityClickHandlerOptions) => {
    if (!options?.type) {
      throw new Error('Activity type must be provided to Activities click handler');
    }

    const allowedTypes: ActivityTypeName[] = ['Autotask', 'Note', 'Reminder', 'Task'];

    if (!allowedTypes.includes(options.type)) {
      throw new Error(`${options.type} is not a valid type. 'type' must be one of ${allowedTypes.join(', ')}`);
    }
  };

  const addActivityClickHandler = (options: ActivityClickHandlerOptions, setupCallback: AddActivitySetupCallback, activity?: Activity) => {
    if (typeof setupCallback !== 'function') {
      throw new Error('`setupCallback` must be a function');
    }

    validateOptions(options);
    setCurrentAction(options.type, Boolean(activity));

    const typeSelection = getActivityType(options.type);

    if (!typeSelection) {
      throw new Error(`Unable to load the selection value for ${options.type}. Has the useSelectionsStore been initialized?`);
    }

    const modelOptions = getModelOptions(options.view, options.parentObject);
    modelOptions.type = typeSelection;

    const [model, changes] = getModel(modelOptions, activity);

    setupCallback(model, changes);
  };

  const deleteActivity = async (id: number, deleteSeries?: boolean) => {
    if (!id) return;

    const url = deleteSeries ? `/crm/activities/${id}/series` : `/crm/activities/${id}`;

    const deletedIdx = allActivities.value.findIndex(act => act.id === id);

    try {
      await destroy(url);

      onActivityDeleted?.(id);

      if (deletedIdx !== -1) {
        allActivities.value.splice(deletedIdx, 1);
      }
    } catch {
      showError(`Unable to delete ${allActivities.value[deletedIdx]?.title ?? 'Activity'}`);
    }
  };

  const snoozeReminder = async (activity: Activity, reminderId: number, forMinutes: number) => {
    const reminder = activity?.reminders?.find(r => r.id === reminderId);
    if (!reminder) {
      throw new Error('Reminder not found');
    }

    const now = new Date();
    // When minutes are positive, we're snoozing for that many minutes
    // When minutes are 0, we're snoozing until activity start time
    // When minutes are negative, we're snoozing until that many minutes before start time
    if (forMinutes > 0) {
      const fireAt = addMinutes(now, forMinutes);

      reminder.minutesBefore = forMinutes;
      reminder.fireAt = fireAt.toISOString();
    } else if (forMinutes <= 0 && parse(activity.startDateTime)) {
      const startTime = adjustUtcToLocal(parse(activity.startDateTime));
      const fireAt = subMinutes(startTime, Math.abs(forMinutes));

      reminder.minutesBefore = Math.abs(forMinutes);
      reminder.fireAt = fireAt.toISOString();
    }

    if (activity.type.name === 'Reminder') {
      activity.startDateTime = reminder.fireAt;
    }

    const result = await createOrUpdateActivity(activity, true);

    return result;
  };

  const deleteReminder = async (activity: Activity, reminderId: number) => {
    const idx = activity?.reminders?.findIndex(reminder => reminder.id === reminderId);
    if (idx === -1) {
      throw new Error('Reminder not found');
    }

    activity.reminders.splice(idx, 1);

    const result = await createOrUpdateActivity(activity, true);

    return result;
  };

  const markReminderViewed = async (activity: Activity, reminderId: number) => {
    const reminder = activity?.reminders?.find(r => r.id === reminderId);
    if (!reminder) {
      throw new Error('Reminder not found');
    }

    reminder.viewedAt = new Date().toISOString();

    const result = await createOrUpdateActivity(activity, true);

    return result;
  };

  const markAllRemindersViewed = async (reminderIds: number[]) => {
    const payload = {
      data: {
        reminders: reminderIds,
      },
    };

    const result = await viewAllRemindersRequest(payload);
    return Boolean(result?.success);
  };

  const dismissAllReminders = async (reminderIds: number[]) => {
    const payload = {
      data: {
        reminders: reminderIds,
      },
    };

    const result = await deleteAllRemindersRequest(payload);
    return Boolean(result?.success);
  };

  const formatReminderTitle = (activity: Activity) => {
    if (activity.title) return activity.title;

    if (arrayHasValues(activity.contacts)) {
      const [contact] = activity.contacts;
      return `Reminder - ${contact.name}`;
    }

    if (arrayHasValues(activity.accounts)) {
      const [account] = activity.accounts;
      return `Reminder - ${account.name}`;
    }

    if (arrayHasValues(activity.campaigns)) {
      const [campaign] = activity.campaigns;
      return `Reminder - ${campaign.name}`;
    }

    return 'Reminder';
  };

  const formatReminderDueAt = (activity: Activity) => {
    const noDate = 'No due date';
    if (!activity?.startDateTime) return noDate;

    const startTime = adjustUtcToLocal(parse(activity.startDateTime));
    if (!startTime) {
      return noDate;
    }

    const now = new Date();

    return isBefore(startTime, now)
      ? `Due ${formatDistanceToNowStrict(startTime)} ago`
      : `Due in ${formatDistanceToNowStrict(startTime)}`;
  };

  const findParentRelationship = (activity: Activity) => {
    const account = activity.accounts?.find(a => a.isParent);
    if (account) {
      return account;
    }

    const contact = activity.contacts?.find(c => c.isParent);
    if (contact) {
      return contact;
    }

    const campaign = activity.campaigns?.find(c => c.isParent);
    if (campaign) {
      return campaign;
    }

    const hasAccount = arrayHasValues(activity.accounts);
    const hasCampaign = arrayHasValues(activity.campaigns);
    const hasContact = arrayHasValues(activity.contacts);

    if (hasAccount) {
      return activity.accounts[0];
    }

    if (hasContact) {
      return activity.contacts[0];
    }

    if (hasCampaign) {
      return activity.campaigns[0];
    }

    return undefined;
  };

  const confirmDeleteActivity = (isSeries: boolean, acceptFn: () => void) => {
    const message = isSeries
      ? 'Are you sure you want to delete this activity, as well as all future scheduled activities?'
      : 'Are you sure you want to delete this activity?';

    confirm.require({
      accept: () => acceptFn(),
      acceptLabel: 'Yes, delete',
      acceptProps: { icon: TrashIcon, severity: 'danger', outlined: true },
      group: DialogGroups.crm.activities.confirmDeleteActivity,
      header: 'Confirm delete',
      message,
      rejectLabel: 'Cancel',
      rejectProps: { severity: 'secondary', outlined: true },
    });
  };

  const isActivity = (activity: Activity) => activity?.type?.name?.toLowerCase() === 'task';
  const isNote = (activity: Activity) => activity?.type?.name?.toLowerCase() === 'note';
  const isReminder = (activity: Activity) => activity?.type?.name?.toLowerCase() === 'reminder';
  const isSystem = (activity: Activity) => activity?.type?.name?.toLowerCase() === 'system';

  const insertOrUpdateActivityInList = (activity: Activity) => {
    const idx = allActivities.value?.findIndex(a => a.id === activity.id);
    if (idx === -1) {
      allActivities.value.push({ ...activity });
    } else {
      allActivities.value[idx] = { ...allActivities.value[idx], ...activity };
    }
  };

  const removeActivityFromList = (activity: number | Activity) => {
    const id = typeof activity === 'number' ? activity : activity.id;
    const idx = allActivities.value?.findIndex(a => a.id === id);
    if (idx !== -1) {
      allActivities.value.splice(idx, 1);
    }
  };

  const getActivityTitle = (activity: Activity) => {
    if (activity?.title) return activity.title;
    if (activity?.type?.displayName) return activity.type.displayName;
    return undefined;
  };

  const navigateToActivityFlyout = (activity: Activity) => {
    const parent = findParentRelationship(activity);

    if (!parent) {
      throw new Error('Unable to navigate because activity has no relationships');
    }

    const parentType = relationshipTypeToCrmParent.parse(parent.type);
    const detailType = relationshipTypeToCrmDetail.parse(parent.type);

    const shouldKeepParent =
      route.path.startsWith('/crm/accounts') ||
      route.path.startsWith('/crm/contacts') ||
      route.path.startsWith('/crm/leads') ||
      route.path.startsWith('/crm/opportunities');

    openCrmDetailFlyout(parentType, detailType, parent.id, 'activity', shouldKeepParent);
  };

  const lastTask = computed<Activity>(() => {

    const pastActivities = allActivities.value?.filter(
      activity => isActivity(activity) && activity.completedAt,
    );

    if (!arrayHasValues(pastActivities)) {
      return undefined;
    }

    // Return most recently completed activity object
    return pastActivities.reduce((prev, curr) => parse(prev.completedAt) > parse(curr.completedAt) ? prev : curr);
  });

  const upcomingTask = computed<Activity>(() => {
    const now = new Date();

    const upcomingActivities = allActivities.value?.filter(
      activity => isActivity(activity) && activity.startDateTime && !activity.completedAt && isAfter(adjustUtcToLocal(parse(activity.startDateTime)), now),
    );

    if (!arrayHasValues(upcomingActivities)) {
      return undefined;
    }

    // Return nearest upcoming Activity that is not completed
    return upcomingActivities.reduce((prev, curr) => new Date(prev.startDateTime) < new Date(curr.startDateTime) ? prev : curr);
  });

  const getFormattedMiniCardTitle = (activity: Activity, ignoreCurrentYear: boolean = true): string => {
    // Used both by MiniCard Last/Upcoming Activity. Last relies on non-null completedAt. Upcoming relies on non-null startDateTime.
    const taskDate = activity.completedAt ? adjustUtcToLocal(parse(activity.completedAt)).toISOString() : adjustUtcToLocal(parse(activity.startDateTime)).toISOString();
    if (!taskDate) {
      return '';
    }

    const formatString = isInCurrentYear(taskDate) && ignoreCurrentYear ? 'MMMM do' : 'MMMM do, yyyy';

    return `${activity.method.displayName} on: ${ formatDate(taskDate, formatString, '') }`;
  };

  const hasRelationships = (activity: Activity) => arrayHasValues(activity?.accounts) || arrayHasValues(activity?.campaigns) || arrayHasValues(activity?.contacts);

  const getDisplayType = (activity: Activity | ActivityTypeName) => {
    if (!activity || (typeof activity === 'object' && !activity.type?.name)) return '';
    const type = typeof activity === 'string' ? activity : activity.type.name;

    return type.toLowerCase() === 'task' ? 'Activity' : type;
  };

  const emptyActivityFilter = createObjectFromSchema(activityFilter);

  const getFilteredActivities = async (filter: ActivityFilter): Promise<ActivityFilterResponse> => {
    try {
      const filterData = Object.fromEntries(
        Object.keys(filter).filter(key =>
          // key in emptyActivityFilter is a little trick to exclude saved filter fields that aren't set to the backend filter endpoint
          (key in emptyActivityFilter) && Object.hasOwn(filter, key) && ((filter[key] !== '' && filter[key] !== undefined && !Array.isArray(filter[key])) || (Array.isArray(filter[key]) && arrayHasValues(filter[key]))),
        )
          .map(key => [key, filter[key]]),
      );

      const response = await activityFilterEndpoint.getResults({ filter: filterData });
      if (response?.data?.activities) {
        return {
          activities: response.data.activities.map(activity => populateFeFieldsOnActivityResponse.parse(activity)),
          meta: {
            nextCursor: response.meta?.nextCursor,
          },
        };
      }
    } catch (e) {
      developerWarning(true, e, 'throw');
      showError('We were unable to load your Activities. Refresh the page to try again, or contact Support if you continue to experience the issue', 'Error loading Activities');
    }

    return {
      activities: [],
      meta: {
        nextCursor: undefined,
      },
    };
  };

  const processSavedFilterResponse = (response: SavedFilterResponse): SavedActivityFilter[] => {
    if (!arrayHasValues(response?.data?.filters)) return undefined;

    const { filters } = response.data;
    const userDefined = new Set([...filters.filter(f => Boolean(f.userId)).map(f => f.filter.key)]);

    return filters.filter(f => !f.filter.overrides && ((!f.userId && !userDefined.has(f.filter.key)) || Boolean(f.userId)))
      .map(f => ({ ...f.filter, id: f.id }));
  };

  const getSavedFilters = async (): Promise<SavedActivityFilter[]> => {
    try {
      const result = await getSavedFilterEndpoint.getResults();
      return processSavedFilterResponse(result);
    } catch (e) {
      developerWarning(true, e, 'throw');
      showError('There was an error loading your Activities. Please refresh the page to try again, or contact support');
    }

    return undefined;
  };

  const getBackendFilterFields = () => {
    const obj = createObjectFromSchema(activityFilter);
    return Object.keys(obj).filter(key => Object.hasOwn(obj, key));
  };

  const saveFilters = async (filters: SavedActivityFilter[]): Promise<SavedActivityFilter[]> => {
    const objectShape = createObjectFromSchema(savableActivityFilter);

    const excludeFields = new Set<keyof SavedActivityFilter>(['nextCursor', 'title']);

    const payload = {
      filters: filters.map(filter => Object.fromEntries(
        Object.keys(filter).filter(key => Object.hasOwn(filter, key) && (key in objectShape) && !excludeFields.has(key as keyof SavedActivityFilter)).map(key => {
          if (key === 'key') {
            if (filter[key]) {
              return [key, filter[key]];
            }
            return [key, crypto.randomUUID()];
          }
          return [key, filter[key]];
        }),
      )),
    };

    try {
      const result = await saveFilterEndpoint.getResults(payload);
      if (result?.data) {
        return processSavedFilterResponse(result);
      }
    } catch (e) {
      developerWarning(true, e, 'throw');
      showError('There was an error saving your settings. Please try again, or contact support if you continue to experience errors');
    }

    return undefined;
  };

  /**
   * The UI uses a range slider control for these to return a [number, number] tuple
   * these numbers are used for when the user wants to see activities from the previous/next
   * days instead of activities from a specific date range. These numbers are then used at
   * runtime to dynamically create a date which is sent to the BE for min/max activity start time
   * These magic numbers are used for special cases where the number does something other than just
   * translate to n days ago, y days ahead
   */
  const filterMagicNumbers = {
    noMinimum: -15,
    noMaximum: 16,
    now: 0,
    today: 1,
  };

  const getDateFromDynamicRange = (range: number, kind: 'min' | 'max'): Date => {
    if (range === filterMagicNumbers.now) {
      return new Date();
    }

    if (range === filterMagicNumbers.today) {
      return kind === 'min' ? startOfDay(new Date()) : endOfDay(new Date());
    }

    // Since the UI control increments in units of 1, this allows for treating 1 as "today" and 2 as tomorrow, instead of 2 as two days away
    const adjustedRange = range < 0 ? range : range - 1;

    return kind === 'min'
      ? startOfDay(addDays(new Date(), adjustedRange))
      : endOfDay(addDays(new Date(), adjustedRange));
  };

  const transformVariableFilterDaysToFixedDates = (savedFilter: SavedActivityFilter): ActivityFilter => {
    if (!arrayHasValues(savedFilter.dayRange, { exactly: 2 })) {
      return savedFilter;
    }

    const [start, end] = savedFilter.dayRange;

    const minDate = getDateFromDynamicRange(start, 'min');
    const maxDate = getDateFromDynamicRange(end, 'max');

    const result = { ...savedFilter };
    result.minActivityDate = start === filterMagicNumbers.noMinimum ? undefined : minDate.toISOString();
    result.maxActivityDate = end === filterMagicNumbers.noMaximum ? undefined : maxDate.toISOString();

    return result;
  };

  const getLabelForVariableDayRange = (range: number, kind: 'min' | 'max') => {
    if (isNaN(range)) {
      return `no ${kind === 'min' ? 'minimum' : 'maximum'}`;
    }

    if (range === filterMagicNumbers.noMinimum) {
      return 'no minimum';
    }

    if (range === filterMagicNumbers.noMaximum) {
      return 'no maximum';
    }

    if (range === filterMagicNumbers.now) {
      return 'now';
    }

    if (range === filterMagicNumbers.today) {
      return 'today';
    }

    if (range === filterMagicNumbers.today + 1) {
      return 'tomorrow';
    }

    if (range > filterMagicNumbers.today + 1) {
      return `next ${range - 1} days`;
    }

    if (range === -1) {
      return 'yesterday';
    }

    return `previous ${Math.abs(range)} days`;
  };

  const resetActivityFiltersToDefault = async () => {
    try {
      const response = await resetFilterEndpoint.getResults();
      return response?.success;
    } catch {
      showError('There was an error resetting your filters. Please try again, or contact support');
      return false;
    }
  };

  const getToastReminder = (activity: Activity) => {
    const toastType = selections.activities.reminderTypes.find(r => r.name === ReminderTypes.Toast);
    return activity.reminders?.find(r => r.type.id === toastType.id);
  };

  const getNotificationCenterReminder = (activity: Activity) => {
    const centerType = selections.activities.reminderTypes.find(r => r.name === ReminderTypes.Dashboard);
    return activity.reminders?.find(r => r.type.id === centerType.id);
  };

  const openDeferReminderDialog = (activity: Activity) => dialog.open(DeferReminderComponent, {
    props: {
      header: 'Defer until',
    },
    data: {
      minDate: activity.startDateTime ? adjustUtcToLocal(parse(activity.startDateTime)) : new Date(),
    },
    emits: {
      onSave: async (deferUntil: Date) => {
        const payload: Partial<Activity> = {
          id: activity.id,
          startDateTime: deferUntil.toISOString(),
        };

        if (activity.type.name === 'Reminder') {
          const toastReminder = getToastReminder(activity);
          const notificationCenterReminder = getNotificationCenterReminder(activity);

          payload.reminders = [];

          if (toastReminder) {
            toastReminder.fireAt = deferUntil.toISOString();
            payload.reminders.push(toastReminder);
          }

          if (notificationCenterReminder) {
            notificationCenterReminder.fireAt = deferUntil.toISOString();
            payload.reminders.push(notificationCenterReminder);
          }
        }

        const updated = await createOrUpdateActivity(payload);
        insertOrUpdateActivityInList(updated);
      },
    },
  });

  const postActivity = async (methodId: number, modelId: number, modelName: string, view: SupportedActivitiesView) => {
    const method = selections.activities.methods.find(m => m.id === methodId);
    const displayName = method.displayName;
    const name = !isEmptyString(modelName) ? modelName : `Unnamed ${capitalize(pluralToSingularTransformer.parse(view.toLowerCase()) as CrmParentName)}`;
    const type = selections.activities.types.find(t => t.name === 'Task');
    const relationships = await relationshipStore.getRelationships(view, modelId);
    const campaigns = combineArrays(relationships.Opportunity, relationships.Lead);
    // Best match to use pre-existing method to get most recently Campaign
    const newestCampaign = getMostRecentlyCreatedRelationship(campaigns, ['Lead', 'Opportunity']);
    createOrUpdateActivity({
      accounts: view === 'accounts' ? [{
        id: modelId, name: modelName, type: 'Account', isParent: true,
      }] : relationships.Account,
      // Campaigns cannot be parent in this context, as it's based off Email/Call functionality
      campaigns: newestCampaign ? [newestCampaign] : [],
      contacts: view === 'contacts' ? [{
        id: modelId, name: modelName, type: 'Contact', isParent: true,
      }] : relationships.Contact,
      title: `${displayName} to ${name}`,
      type,
      method,
    });
  };

  const shouldSkipLegacyEmailActivity = async (): Promise<boolean> => {
    const response = await getEmailActivitySyncStatus();

    return response?.shouldSkipLegacyEmailActivity === true;
  };

  const postEmailActivityUnlessSynced = async (methodId: number, modelId: number, modelName: string, view: SupportedActivitiesView) => {
    if (await shouldSkipLegacyEmailActivity()) {
      return;
    }

    await postActivity(methodId, modelId, modelName, view);
  };

  const isEmailActivity = (activity: Activity) => arrayHasValues(activity?.emails) && activity?.type?.name === 'Autotask';

  const getEmailActivityTitle = (activity: Activity) => !isEmptyString(activity.emails[0].subject, true) ? activity.emails[0].subject : 'Email';

  type CreateActivityModalField =
    | 'completed'
    | 'date'
    | 'delete'
    | 'files'
    | 'method'
    | 'note'
    | 'purpose'
    | 'relationships'
    | 'reminder'
    | 'title';

  const shouldDisableField = (field: CreateActivityModalField, activity: Activity): boolean => {
    const isComplete = Boolean(activity.completedAt);
    const isEmail = isEmailActivity(activity);

    if (!isEmail && !isComplete) {
      return false;
    }

    const forbiddenCompletedActivityFields: CreateActivityModalField[] = ['date', 'files', 'method', 'purpose', 'relationships', 'reminder', 'title'];
    const forbiddenEmailActivityFields: CreateActivityModalField[] = ['completed', 'date', 'method'];

    if (isEmail) {
      return forbiddenEmailActivityFields.includes(field);
    }

    return forbiddenCompletedActivityFields.includes(field);
  };

  return {
    activityTabSections,
    addActivityClickHandler,
    completeActivity,
    confirmDeleteActivity,
    createOrUpdateActivity,
    deleteActivity,
    deleteReminder,
    dismissAllReminders,
    emptyActivityFilter,
    filterMagicNumbers,
    findParentRelationship,
    formatReminderDueAt,
    formatReminderTitle,
    getActivityTitle,
    getActivityType,
    getBackendFilterFields,
    getDisplayType,
    getEmailActivityTitle,
    getFilteredActivities,
    getFormattedMiniCardTitle,
    getIconForActivityType,
    getIconForMethod,
    getLabelForVariableDayRange,
    getModel,
    getModelOptions,
    getModelOptionsForEdit,
    getSavedFilters,
    hasRelationships,
    history,
    insertOrUpdateActivityInList,
    isActivity,
    isDeferrable,
    isEmailActivity,
    isLoading,
    isNote,
    isPartOfSeries,
    isReminder,
    isSystem,
    lastTask,
    loadActivities,
    markAllRemindersViewed,
    markReminderViewed,
    modalFieldConfig,
    modalTitle,
    navigateToActivityFlyout,
    openDeferReminderDialog,
    overdue,
    populatePendingRelationships,
    postEmailActivityUnlessSynced,
    postActivity,
    reminderTimeSelections,
    removeActivityFromList,
    resetActivityFiltersToDefault,
    saveFilters,
    setCurrentAction,
    shouldDisableField,
    snoozeReminder,
    transformVariableFilterDaysToFixedDates,
    upcoming,
    upcomingTask,
  };
};
